/**
 * Knowledge Base Controller Module
 * Handles Knowledge Base page initialization
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only KB page logic
 * - Delegation: Delegates to KnowledgeTab module
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import { safeFeatherReplace } from '../../utils/feather-helper';

/**
 * KB Document structure
 */
interface KBDocument {
    id: string;
    title: string;
    summary: string;
    content: string;
    doc_type: any;
    severity?: string[];
    component: string;
    version: string;
    related_docs?: string[];
    created_at: bigint;
    updated_at: bigint;
}

/**
 * KnowledgeBaseController class
 * Manages the knowledge base page
 */
export class KnowledgeBaseController extends BaseController {
    constructor(services: ControllerServices) {
        super(services);
    }

    getPageName(): PageName {
        return 'knowledge';
    }

    protected async doInit(): Promise<void> {
        // Use the global KnowledgeTab instance if available
        if ((window as any).knowledgeTab) {
            await (window as any).knowledgeTab.init();
        } else {
            // Fallback: load documents directly
            await this.loadKBDocuments();
        }
    }

    /**
     * Load and display KB documents
     */
    private async loadKBDocuments(): Promise<void> {
        const mainContent = document.getElementById('kb-main-content');
        if (!mainContent) return;

        this.showLoading('kb-main-content', 'Loading documents...');

        try {
            const allDocs = await this.canisterClient.listKBDocuments();

            // Group documents by type
            const errors = allDocs.filter((doc: KBDocument) => 'Error' in doc.doc_type);
            const resolutions = allDocs.filter((doc: KBDocument) => 'Resolution' in doc.doc_type);
            const bestPractices = allDocs.filter((doc: KBDocument) => 'BestPractice' in doc.doc_type);
            const adrs = allDocs.filter((doc: KBDocument) => 'ADR' in doc.doc_type);

            mainContent.innerHTML = `
                <div class="space-y-6">
                    ${this.renderKBSection('Errors', errors, 'alert-circle', 'red')}
                    ${this.renderKBSection('Resolutions', resolutions, 'check-circle', 'green')}
                    ${this.renderKBSection('Best Practices', bestPractices, 'book', 'purple')}
                    ${this.renderKBSection('Architecture Decisions', adrs, 'git-branch', 'yellow')}
                </div>
            `;

            safeFeatherReplace();
        } catch (error) {
            console.error('Error loading KB documents:', error);
            mainContent.innerHTML = `
                <div class="text-center text-red-500 py-8">
                    <p>Error loading documents: ${error instanceof Error ? error.message : 'Unknown error'}</p>
                </div>
            `;
        }
    }

    /**
     * Render a section of KB documents
     */
    private renderKBSection(title: string, docs: KBDocument[], icon: string, color: string): string {
        if (docs.length === 0) {
            return `
                <div class="border rounded-lg p-6 bg-gray-50">
                    <div class="flex items-center gap-2 mb-4">
                        <i data-feather="${icon}" class="text-${color}-600"></i>
                        <h2 class="text-xl font-bold">${title} (0)</h2>
                    </div>
                    <p class="text-gray-500 text-sm">No documents found.</p>
                </div>
            `;
        }

        return `
            <div class="border rounded-lg p-6 bg-white">
                <div class="flex items-center gap-2 mb-4">
                    <i data-feather="${icon}" class="text-${color}-600"></i>
                    <h2 class="text-xl font-bold">${title} (${docs.length})</h2>
                </div>
                <div class="space-y-3">
                    ${docs.map(doc => this.renderKBDocumentCard(doc, color)).join('')}
                </div>
            </div>
        `;
    }

    /**
     * Render a single KB document card
     */
    private renderKBDocumentCard(doc: KBDocument, color: string): string {
        const updatedDate = new Date(Number(doc.updated_at) / 1000000).toLocaleDateString();
        const severity = doc.severity && doc.severity.length > 0 ? doc.severity[0] : null;

        return `
            <div class="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer" onclick="window.pageManager?.viewKBDocument('${doc.id}')">
                <div class="flex items-start justify-between">
                    <div class="flex-1">
                        <h3 class="font-semibold text-lg mb-1">${this.escapeHtml(doc.id)}: ${this.escapeHtml(doc.title)}</h3>
                        <p class="text-sm text-gray-600 mb-2">${this.escapeHtml(doc.summary)}</p>
                        <div class="flex gap-2 text-xs text-gray-500">
                            ${severity ? `<span class="px-2 py-1 rounded bg-${color}-100 text-${color}-700 font-medium">Severity: ${severity}</span>` : ''}
                            <span class="px-2 py-1 rounded bg-gray-100">Component: ${this.escapeHtml(doc.component)}</span>
                            <span class="px-2 py-1 rounded bg-gray-100">Updated: ${updatedDate}</span>
                            <span class="px-2 py-1 rounded bg-gray-100">Version: ${this.escapeHtml(doc.version)}</span>
                        </div>
                    </div>
                    <button class="btn btn-sm btn-primary ml-4">
                        View
                    </button>
                </div>
            </div>
        `;
    }

    /**
     * View a specific KB document
     */
    public async viewKBDocument(docId: string): Promise<void> {
        console.log(`Viewing KB document: ${docId}`);
        const mainContent = document.getElementById('kb-main-content');
        if (!mainContent) return;

        this.showLoading('kb-main-content', 'Loading document...');

        try {
            const doc = await this.canisterClient.getKBDocument(docId);

            const updatedDate = new Date(Number(doc.updated_at) / 1000000).toLocaleString();
            const createdDate = new Date(Number(doc.created_at) / 1000000).toLocaleString();
            const severity = doc.severity && doc.severity.length > 0 ? doc.severity[0] : null;

            mainContent.innerHTML = `
                <div class="space-y-6">
                    <button class="btn btn-secondary" onclick="window.pageManager?.initKnowledge()">
                        <i data-feather="arrow-left"></i>
                        Back to Knowledge Base
                    </button>

                    <div class="bg-white border rounded-lg p-6">
                        <h1 class="text-3xl font-bold mb-4">${this.escapeHtml(doc.id)}: ${this.escapeHtml(doc.title)}</h1>

                        <div class="flex flex-wrap gap-2 mb-6">
                            ${severity ? `<span class="px-3 py-1 rounded-full bg-red-100 text-red-700 font-medium">Severity: ${severity}</span>` : ''}
                            <span class="px-3 py-1 rounded-full bg-blue-100 text-blue-700">Component: ${this.escapeHtml(doc.component)}</span>
                            <span class="px-3 py-1 rounded-full bg-gray-100">Version: ${this.escapeHtml(doc.version)}</span>
                            <span class="px-3 py-1 rounded-full bg-gray-100">Created: ${createdDate}</span>
                            <span class="px-3 py-1 rounded-full bg-gray-100">Updated: ${updatedDate}</span>
                        </div>

                        ${doc.related_docs && doc.related_docs.length > 0 ? `
                            <div class="mb-6 p-4 bg-blue-50 rounded-lg">
                                <h3 class="font-semibold mb-2">Related Documents:</h3>
                                <div class="flex flex-wrap gap-2">
                                    ${doc.related_docs.map((relId: string) => `
                                        <button class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700" onclick="window.pageManager?.viewKBDocument('${relId}')">
                                            ${this.escapeHtml(relId)}
                                        </button>
                                    `).join('')}
                                </div>
                            </div>
                        ` : ''}

                        <div class="prose max-w-none">
                            <pre class="whitespace-pre-wrap text-sm">${this.escapeHtml(doc.content)}</pre>
                        </div>

                        <div class="mt-6 pt-6 border-t flex gap-2">
                            <button class="btn btn-secondary" onclick="window.pageManager?.exportKBDocument('${doc.id}')">
                                <i data-feather="download"></i>
                                Export Markdown
                            </button>
                        </div>
                    </div>
                </div>
            `;

            safeFeatherReplace();
        } catch (error) {
            console.error('Error viewing KB document:', error);
            mainContent.innerHTML = `
                <div class="text-center text-red-500 py-8">
                    <p>Error loading document: ${error instanceof Error ? error.message : 'Unknown error'}</p>
                    <button class="btn btn-secondary mt-4" onclick="window.pageManager?.initKnowledge()">
                        Back to Knowledge Base
                    </button>
                </div>
            `;
            safeFeatherReplace();
        }
    }

    /**
     * Export KB document as markdown file
     */
    public exportKBDocument(docId: string): void {
        this.canisterClient.getKBDocument(docId).then((doc: KBDocument) => {
            const blob = new Blob([doc.content], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${docId}.md`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            console.log(`Exported ${docId}.md`);
        });
    }
}
